/* eslint-disable react/prop-types */
/* eslint-disable react/require-default-props */
import * as React from 'react';
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react';
import { ArrowLeft, ArrowRight, Maximize, Minimize } from 'lucide-react';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { cn } from '../../../tailwind-base/utils';
import { Button } from '../Button';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  wrapperRef: React.RefObject<HTMLDivElement>;
  isFullscreen: boolean;
  setIsFullscreen: React.Dispatch<React.SetStateAction<boolean>>;
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = 'horizontal',
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    _ref
  ) => {
    const { tt } = useUiComponentsInternationalContext();
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const [isFullscreen, setIsFullscreen] = React.useState(false);

    const onSelect = React.useCallback((carouselApi: CarouselApi) => {
      if (!carouselApi) {
        return;
      }

      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = React.useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollPrev();
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext]
    );

    React.useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) {
        return undefined;
      }

      onSelect(api);
      api.on('reInit', onSelect);
      api.on('select', onSelect);

      // eslint-disable-next-line consistent-return
      return () => {
        api?.off('select', onSelect);
      };
    }, [api, onSelect]);

    React.useEffect(() => {
      const handleChange = () => {
        setIsFullscreen(!!document.fullscreenElement);
      };

      document.addEventListener('fullscreenchange', handleChange);
      return () => {
        document.removeEventListener('fullscreenchange', handleChange);
      };
    }, []);

    const contextValue = React.useMemo(
      () => ({
        carouselRef,
        wrapperRef,
        api,
        opts,
        orientation:
          orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
        isFullscreen,
        setIsFullscreen,
      }),
      [
        carouselRef,
        api,
        opts,
        orientation,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
        isFullscreen,
        setIsFullscreen,
      ]
    );

    return (
      <CarouselContext.Provider value={contextValue}>
        <div
          ref={wrapperRef}
          onKeyDownCapture={handleKeyDown}
          className={cn(
            'relative transition-all',
            isFullscreen &&
              'fixed inset-0 z-[9999] bg-black flex items-center justify-center',
            className
          )}
          aria-label={tt(s => s.shadcnCommon.carouselAriaLabel)}
          aria-roledescription={tt(
            s => s.shadcnCommon.carouselAriaRoleDescription
          )}
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  }
);
Carousel.displayName = 'Carousel';

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div ref={carouselRef} className="overflow-hidden w-full">
      <div
        ref={ref}
        className={cn(
          'flex',
          orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
          className
        )}
        {...props}
      />
    </div>
  );
});
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { orientation } = useCarousel();
  const { tt } = useUiComponentsInternationalContext();

  return (
    <div
      ref={ref}
      aria-label={tt(s => s.shadcnCommon.carouselSectionAriaLabel)}
      aria-roledescription={tt(s => s.shadcnCommon.slideAriaRoleDescription)}
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className
      )}
      {...props}
    />
  );
});
CarouselItem.displayName = 'CarouselItem';

const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();
  const { tt } = useUiComponentsInternationalContext();

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      aria-label="Previous Slide"
      className={cn(
        'absolute h-8 w-8 rounded-full',
        orientation === 'horizontal'
          ? '-left-12 top-1/2 -translate-y-1/2'
          : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
        className
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ArrowLeft className="h-4 w-4" />
      <span className="sr-only">
        {tt(s => s.shadcnCommon.previousSlideButton)}
      </span>
    </Button>
  );
});
CarouselPrevious.displayName = 'CarouselPrevious';

const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
  const { orientation, scrollNext, canScrollNext } = useCarousel();
  const { tt } = useUiComponentsInternationalContext();

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      aria-label="Next Slide"
      className={cn(
        'absolute h-8 w-8 rounded-full',
        orientation === 'horizontal'
          ? '-right-12 top-1/2 -translate-y-1/2'
          : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
        className
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <ArrowRight className="h-4 w-4" />
      <span className="sr-only">{tt(s => s.shadcnCommon.nextSlideButton)}</span>
    </Button>
  );
});
CarouselNext.displayName = 'CarouselNext';

const CarouselFullscreen = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
  const { wrapperRef, isFullscreen, setIsFullscreen } = useCarousel();

  const toggleFullscreen = () => {
    if (!wrapperRef.current) return;

    if (!isFullscreen) {
      wrapperRef.current.requestFullscreen?.();
    } else if (document.fullscreenElement) {
      document.exitFullscreen?.();
    }

    setIsFullscreen(!isFullscreen);
  };

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      aria-label="Enter fullscreen view"
      aria-pressed={isFullscreen}
      className={cn('z-[1] h-8 w-8 rounded-full absolute', className)}
      onClick={toggleFullscreen}
      {...props}
    >
      {isFullscreen ? (
        <Minimize className="h-4 w-4" />
      ) : (
        <Maximize className="h-4 w-4" />
      )}
      <span className="sr-only">
        {isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
      </span>
    </Button>
  );
});
CarouselFullscreen.displayName = 'CarouselFullscreen';

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselFullscreen,
};
